// ═══════════════════════════════════════════════════════════════════
// COMMAND PALETTE — Fuzzy search navigation overlay
// Triggered by Ctrl+K: search and navigate to pages
// Keyboard navigation: up/down arrows, Enter to select
// Glassmorphism styling with GLASS tokens
// ═══════════════════════════════════════════════════════════════════
import { useState, useEffect, useRef, type ComponentType } from "react";
import { type ThemeObject, GLASS, TRANSITION, SP, RADIUS } from "../shared/theme.js";

interface PageEntry {
  id: string;
  label: string;
  Icon?: ComponentType<{ size: number }>;
}

interface CommandPaletteProps {
  T: ThemeObject;
  pages: PageEntry[];
  onNavigate?: (id: string) => void;
  onClose: () => void;
}

// Simple fuzzy search implementation
function fuzzyMatch(query: string, text: string): number | null {
  const lowerQuery = query.toLowerCase();
  const lowerText = text.toLowerCase();
  let score = 0;
  let queryIdx = 0;

  for (let i = 0; i < lowerText.length && queryIdx < lowerQuery.length; i++) {
    if (lowerText[i] === lowerQuery[queryIdx]) {
      score++;
      queryIdx++;
    }
  }

  return queryIdx === lowerQuery.length ? score : null;
}

export default function CommandPalette({ T, pages, onNavigate, onClose }: CommandPaletteProps) {
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Fuzzy search pages
  const results = pages
    .map((page) => {
      const score = fuzzyMatch(query, page.label);
      return { ...page, score };
    })
    .filter((page): page is typeof page & { score: number } => page.score !== null)
    .sort((a, b) => b.score - a.score)
    .slice(0, 10);

  // Reset selection when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev < results.length - 1 ? prev + 1 : prev));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (results[selectedIndex]) {
        onNavigate?.(results[selectedIndex].id);
        onClose?.();
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose?.();
    }
  };

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current) {
      const selectedItem = listRef.current.children[selectedIndex] as HTMLElement | undefined;
      if (selectedItem) {
        selectedItem.scrollIntoView({ block: "nearest" });
      }
    }
  }, [selectedIndex]);

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.6)",
          zIndex: 9998,
          animation: "fade-in 0.2s ease-out",
        }}
        onClick={onClose}
      />

      {/* Palette container */}
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 9999,
          width: "90%",
          maxWidth: 480,
          maxHeight: "70vh",
          background: GLASS.dark.bg,
          backdropFilter: GLASS.dark.blur,
          border: `1px solid ${GLASS.dark.border}`,
          borderRadius: RADIUS.lg,
          boxShadow: GLASS.dark.shadow,
          display: "flex",
          flexDirection: "column",
          animation: "palette-in 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
          fontFamily: "'Inter', sans-serif",
        }}
      >
        {/* Search input */}
        <div style={{ padding: SP[4], borderBottom: `1px solid ${T.border}` }}>
          <input
            ref={inputRef}
            type="text"
            placeholder="Buscar página..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            style={{
              width: "100%",
              background: "transparent",
              border: "none",
              color: T.text,
              fontSize: "16px",
              fontWeight: 500,
              outline: "none",
              padding: 0,
              fontFamily: "inherit",
            }}
          />
        </div>

        {/* Results list */}
        <div
          ref={listRef}
          style={{
            overflowY: "auto",
            flex: 1,
            maxHeight: "calc(70vh - 60px)",
            padding: SP[2],
          }}
        >
          {results.length > 0 ? (
            results.map((page, index) => (
              <button
                key={page.id}
                onClick={() => {
                  onNavigate?.(page.id);
                  onClose?.();
                }}
                onMouseEnter={() => setSelectedIndex(index)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  padding: `${SP[3]} ${SP[4]}`,
                  background: selectedIndex === index ? `rgba(204, 133, 68, 0.15)` : "transparent",
                  border: "none",
                  borderRadius: RADIUS.md,
                  color: T.text,
                  textAlign: "left",
                  cursor: "pointer",
                  transition: TRANSITION.fast,
                  marginBottom: SP[1],
                  fontSize: "14px",
                  fontWeight: 500,
                }}
              >
                {page.Icon && (
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginRight: SP[3],
                      opacity: 0.6,
                      flexShrink: 0,
                    }}
                  >
                    <page.Icon size={16} />
                  </span>
                )}
                <span style={{ flex: 1 }}>{page.label}</span>
                <span style={{ fontSize: "11px", color: T.muted, marginLeft: SP[2] }}>
                  {page.id}
                </span>
              </button>
            ))
          ) : (
            <div
              style={{
                padding: SP[6],
                textAlign: "center",
                color: T.muted,
                fontSize: "13px",
              }}
            >
              Nenhuma página encontrada
            </div>
          )}
        </div>

        {/* Footer with hints */}
        <div
          style={{
            padding: SP[3],
            borderTop: `1px solid ${T.border}`,
            fontSize: "11px",
            color: T.muted,
            display: "flex",
            gap: SP[4],
            justifyContent: "flex-end",
            flexWrap: "wrap",
          }}
        >
          <span>↑↓ Navegar</span>
          <span>Enter Selecionar</span>
          <span>Esc Fechar</span>
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes palette-in {
          from {
            opacity: 0;
            transform: translate(-50%, -50%) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
          }
        }
      `}</style>
    </>
  );
}
